import { Link } from "react-router-dom";
import { HugeiconsIcon } from "@hugeicons/react";
import type { IconSvgElement } from "@hugeicons/react";
import {
  Video01Icon,
  ShoppingBag01Icon,
  MusicNote01Icon,
  InstagramIcon,
  YoutubeIcon,
  PodcastIcon,
  Megaphone01Icon,
  BrushIcon,
  DocumentAttachmentIcon,
  ArrowRight01Icon,
} from "@hugeicons/core-free-icons";
import { cn } from "@/lib/utils";

interface Template {
  title: string;
  description: string;
  icon: IconSvgElement;
  modules: number;
  category: "Video Creation" | "Social Media" | "Marketing";
}

const templates: Template[] = [
  {
    title: "YouTube Shorts",
    description:
      "Generate YouTube Shorts with AI-scripted scenes, generated visuals, and voiceover.",
    icon: Video01Icon,
    modules: 7,
    category: "Video Creation",
  },
  {
    title: "Music Video Generator",
    description:
      "Create visual music videos from a text prompt with scene generation and beat-synced editing.",
    icon: MusicNote01Icon,
    modules: 6,
    category: "Video Creation",
  },
  {
    title: "Product Showcase",
    description:
      "Turn product photos into polished video showcases with transitions, music, and captions.",
    icon: ShoppingBag01Icon,
    modules: 5,
    category: "Video Creation",
  },
  {
    title: "Instagram Reels",
    description:
      "Transform a topic into a scroll-stopping Reel with voiceover, captions, and visuals.",
    icon: InstagramIcon,
    modules: 6,
    category: "Social Media",
  },
  {
    title: "YouTube Long-Form",
    description:
      "Script, narrate, and assemble a full-length YouTube video from a single prompt.",
    icon: YoutubeIcon,
    modules: 9,
    category: "Social Media",
  },
  {
    title: "Podcast Clip Generator",
    description:
      "Extract highlight clips from a podcast transcript with subtitles and b-roll.",
    icon: PodcastIcon,
    modules: 5,
    category: "Social Media",
  },
  {
    title: "Ad Creative Generator",
    description:
      "Generate on-brand ad visuals and copy for multiple platforms from a product brief.",
    icon: Megaphone01Icon,
    modules: 8,
    category: "Marketing",
  },
  {
    title: "Brand Asset Kit",
    description:
      "Produce a full set of branded visuals — banners, thumbnails, and social headers.",
    icon: BrushIcon,
    modules: 6,
    category: "Marketing",
  },
  {
    title: "Case Study Builder",
    description:
      "Turn raw notes into a structured case study with visuals, quotes, and formatting.",
    icon: DocumentAttachmentIcon,
    modules: 7,
    category: "Marketing",
  },
];

const categories = ["Video Creation", "Social Media", "Marketing"] as const;

export function TemplatesGrid() {
  return (
    <section className="pb-24 pt-8">
      <div className="mx-auto max-w-5xl px-6">
        {categories.map((category) => (
          <div key={category} className="mb-16">
            <h2 className="text-foreground mb-6 text-sm font-semibold uppercase tracking-widest">
              {category}
            </h2>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
              {templates
                .filter((t) => t.category === category)
                .map((template) => (
                  <div
                    key={template.title}
                    className="group flex flex-col rounded-2xl border border-black/5 bg-card p-6 shadow-sm transition-colors hover:border-black/10 dark:border-white/10 dark:bg-white/5 dark:hover:border-white/20"
                  >
                    <div className="bg-brand-muted flex size-10 items-center justify-center rounded-lg">
                      <HugeiconsIcon
                        icon={template.icon}
                        className="text-brand size-5"
                      />
                    </div>
                    <h3 className="text-foreground mt-4 text-base font-semibold">
                      {template.title}
                    </h3>
                    <p className="text-muted-foreground mt-0.5 text-xs">
                      {template.modules} modules
                    </p>
                    <p className="text-muted-foreground mt-3 flex-1 text-sm leading-relaxed">
                      {template.description}
                    </p>
                    <Link
                      to="/pricing"
                      className={cn(
                        "text-brand mt-5 inline-flex items-center gap-1 text-sm font-medium",
                        "opacity-0 transition-opacity group-hover:opacity-100",
                      )}
                    >
                      Get early access
                      <HugeiconsIcon
                        icon={ArrowRight01Icon}
                        className="size-3.5"
                      />
                    </Link>
                  </div>
                ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
